/*
  Bean & Paw scroll reveals: subtle entrance for the parts of each bp section.
  Plain IntersectionObserver + CSS transitions (no GSAP: the theme keeps its runtime
  dependency set unchanged). Follows gsap-performance rules anyway: transform + opacity
  only, staggered, will-change only while moving, observers cleaned up.

  Safety:
  - Content is never hidden unless this script runs (the hidden state needs html.bp-reveal).
  - Skipped entirely for prefers-reduced-motion, in the theme editor, and on cart pages.
  - The first screen is never animated: items already in view at load are shown at once.
*/
const SELECTOR = [
  '.bp-categories .bp-section-head',
  '.bp-cat',
  // Shop by category (bp-shop-categories) and the lifestyle row: same entrance as the other bp rows.
  '.bp-shop__head',
  '.bp-shop-card',
  '.bp-shop-feature',
  '.bp-life__item',
  '.bp-fc .bp-section-head',
  '.bp-fc .bp-grid > li',
  '.bp-fc__photo',
  '.bp-story__copy',
  '.bp-story__pillar',
  '.bp-newsletter__inner',
  '.bp-trust__item',
  // Motion round (T2 2026-10-19): product page below the fold (tabs, reviews) and the FAQ topic groups.
  // Same rule as everything above: never on first-screen content (items in view at load are left alone).
  '.bp-tabs',
  '.bp-reviews__inner',
  '.bp-faq__group',
  // Journal (fixer GSAP/React Bits audit 2026-10-23, native follow-up 1): the blog listing's cards. Cards that "View
  // more" appends after load are never marked, so they simply appear. The article page's product row and
  // newsletter are already covered by .bp-fc / .bp-newsletter__inner.
  '.blog-post-item',
].join(',');

const STAGGER_MS = 60;
const MAX_STAGGER_STEPS = 5;

function init() {
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
  if (document.body.classList.contains('template-cart') || location.pathname.startsWith('/cart')) return;

  const items = [...document.querySelectorAll(SELECTOR)];
  if (!items.length) return;

  // Anything already on screen stays put: no animation above the fold.
  const vh = window.innerHeight;
  const pending = items.filter((el) => el.getBoundingClientRect().top > vh * 0.92);
  if (!pending.length) return;

  for (const el of pending) el.setAttribute('data-bp-reveal', '');
  root.classList.add('bp-reveal');

  const observer = new IntersectionObserver(
    (entries) => {
      // Stagger the items that enter together, in document order, per section.
      const entering = entries.filter((e) => e.isIntersecting).map((e) => e.target);
      const bySection = new Map();
      for (const el of entering) {
        const key = el.closest('.shopify-section') || document.body;
        if (!bySection.has(key)) bySection.set(key, []);
        bySection.get(key).push(el);
      }
      for (const group of bySection.values()) {
        group.forEach((el, i) => {
          el.style.transitionDelay = `${Math.min(i, MAX_STAGGER_STEPS) * STAGGER_MS}ms`;
          el.classList.add('is-in');
          observer.unobserve(el);
          const done = (event) => {
            if (event.target !== el || event.propertyName !== 'transform') return;
            el.removeEventListener('transitionend', done);
            el.style.transitionDelay = '';
            el.removeAttribute('data-bp-reveal'); // drops will-change once it has moved
            el.classList.remove('is-in');
          };
          el.addEventListener('transitionend', done);
        });
      }
      if (!document.querySelector('[data-bp-reveal]:not(.is-in)')) observer.disconnect();
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  for (const el of pending) observer.observe(el);

  // Safety nets (premium audit #10): never leave a section invisible.
  // 1) Anything the visitor has already scrolled past (End key, anchor jump, fast fling)
  //    is shown without animation. 2) Printing shows everything. 3) An automated or hidden-tab
  //    render that hasn't scrolled after 4 s: everything is shown (see the timer below).
  const showNow = (el) => {
    observer.unobserve(el);
    el.style.transition = 'none';
    el.removeAttribute('data-bp-reveal');
    el.classList.remove('is-in');
  };
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      for (const el of document.querySelectorAll('[data-bp-reveal]:not(.is-in)')) {
        if (el.getBoundingClientRect().bottom < 0) showNow(el);
      }
      if (!document.querySelector('[data-bp-reveal]')) window.removeEventListener('scroll', onScroll);
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('beforeprint', () => document.querySelectorAll('[data-bp-reveal]').forEach(showNow), { once: true });
  // 3) Automated / background renders (headless captures, crawlers, a hidden tab): after 4 s with no scroll,
  //    show everything. Real readers who linger on the hero keep their reveals (T2 2026-10-19: the old
  //    "scrollY == 0 after 4 s" rule switched reveals off for most visitors).
  setTimeout(() => {
    const automated = navigator.webdriver === true || document.visibilityState === 'hidden';
    if (automated && window.scrollY === 0) document.querySelectorAll('[data-bp-reveal]:not(.is-in)').forEach(showNow);
  }, 4000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
